import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { getFirebase, isFirebaseConfigured } from '../firebase/config';
import { writeAudit } from '../lib/audit';
import {
  ensureAdminAllowlistRow,
  googleDisplayName,
  googleEmail,
  loadAccessAdmins,
  tryClaimFirstAdmin,
} from '../lib/bootstrap';
import { decideGate } from '../lib/gate';
import { emailDocId } from '../lib/ids';
import type { AllowlistEntry, Role, UserProfile } from '../types';

type Status = 'loading' | 'signedOut' | 'denied' | 'ready';

type AuthState = {
  status: Status;
  configured: boolean;
  user: User | null;
  profile: UserProfile | null;
  isAdmin: boolean;
  error: string | null;
  signInGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  listAllowlist: () => Promise<AllowlistEntry[]>;
  addInvite: (email: string, role: Role) => Promise<void>;
  removeInvite: (email: string) => Promise<void>;
};

const Ctx = createContext<AuthState | null>(null);

async function readGate(user: User): Promise<{
  status: Exclude<Status, 'loading' | 'signedOut'>;
  profile: UserProfile | null;
}> {
  const email = googleEmail(user);
  if (!email) return { status: 'denied', profile: null };

  const { db } = getFirebase();
  let access = await loadAccessAdmins();
  const inviteSnap = await getDoc(doc(db, 'allowlist', email));

  let decision = decideGate({
    email,
    accessExists: access.exists,
    admins: access.admins,
    invited: inviteSnap.exists(),
  });

  if (decision === 'bootstrap') {
    if (!access.exists) {
      await tryClaimFirstAdmin(user, email);
    } else if (access.admins.includes(email)) {
      await ensureAdminAllowlistRow(user, email);
    }
    access = await loadAccessAdmins();
    const again = await getDoc(doc(db, 'allowlist', email));
    decision = decideGate({
      email,
      accessExists: access.exists,
      admins: access.admins,
      invited: again.exists(),
    });
  }

  if (decision !== 'ready') return { status: 'denied', profile: null };

  const roleSnap = await getDoc(doc(db, 'allowlist', email));
  const role = roleSnap.data()?.role === 'admin' ? 'admin' : 'guest';
  const profile: UserProfile = {
    uid: user.uid,
    email,
    displayName: googleDisplayName(user),
    role,
  };
  await setDoc(
    doc(db, 'users', user.uid),
    {
      email,
      displayName: googleDisplayName(user),
      role,
      lastLoginAt: serverTimestamp(),
    },
    { merge: true },
  );
  return { status: 'ready', profile };
}

function googleProvider() {
  const provider = new GoogleAuthProvider();
  provider.addScope('profile');
  provider.addScope('email');
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const configured = isFirebaseConfigured();
  const [status, setStatus] = useState<Status>(configured ? 'loading' : 'signedOut');
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!configured) return;
    const { auth } = getFirebase();
    return onAuthStateChanged(auth, async (next) => {
      setError(null);
      if (!next) {
        setUser(null);
        setProfile(null);
        setStatus('signedOut');
        return;
      }
      setUser(next);
      setStatus('loading');
      try {
        const gate = await readGate(next);
        setProfile(gate.profile);
        setStatus(gate.status);
        if (next.email) {
          const mail = googleEmail(next);
          if (mail) {
            void writeAudit(
              next.uid,
              mail,
              gate.status === 'ready' ? 'login_ok' : 'login_denied',
            );
          }
        }
      } catch (err) {
        console.error(err);
        setError('Could not verify the invitation list.');
        setStatus('denied');
      }
    });
  }, [configured]);

  const signInGoogle = useCallback(async () => {
    const { auth } = getFirebase();
    setError(null);
    await signInWithPopup(auth, googleProvider());
  }, []);

  const signOut = useCallback(async () => {
    const { auth } = getFirebase();
    await fbSignOut(auth);
  }, []);

  const listAllowlist = useCallback(async () => {
    const { db } = getFirebase();
    const snap = await getDocs(query(collection(db, 'allowlist'), orderBy('email')));
    return snap.docs.map((d) => {
      const data = d.data();
      return {
        email: String(data.email ?? d.id),
        role: data.role === 'admin' ? 'admin' : 'guest',
        createdBy: String(data.createdBy ?? ''),
      } satisfies AllowlistEntry;
    });
  }, []);

  const addInvite = useCallback(async (email: string, role: Role) => {
    if (!user) throw new Error('Not signed in');
    const { db } = getFirebase();
    const id = emailDocId(email);
    await setDoc(doc(db, 'allowlist', id), {
      email: id,
      role,
      createdBy: user.uid,
      createdAt: serverTimestamp(),
    });
    await writeAudit(user.uid, emailDocId(user.email ?? id), 'invite_add', id);
  }, [user]);

  const removeInvite = useCallback(async (email: string) => {
    if (!user?.email) throw new Error('Not signed in');
    const { db } = getFirebase();
    const id = emailDocId(email);
    if (id === emailDocId(user.email)) throw new Error('You cannot remove your own invitation.');
    await deleteDoc(doc(db, 'allowlist', id));
    await writeAudit(user.uid, emailDocId(user.email), 'invite_remove', id);
  }, [user]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      configured,
      user,
      profile,
      isAdmin: profile?.role === 'admin',
      error,
      signInGoogle,
      signOut,
      listAllowlist,
      addInvite,
      removeInvite,
    }),
    [
      status,
      configured,
      user,
      profile,
      error,
      signInGoogle,
      signOut,
      listAllowlist,
      addInvite,
      removeInvite,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
